import { Account, CanvaBrandLink, PartialAccount } from 'domains/accountDomain';
import { Paged } from 'domains/appDomain';
import { SiteRoleEnum } from 'domains/roleDomain';
import api from 'utils/api';
import { UserInviteParams } from './../domains/accountDomain';
import { UserContactAddress } from './../domains/orgDomain';

const accountService = {
  getPagedAccount: async (
    {
      size,
      page,
      email,
    }: {
      size: number;
      page: number;
      email: string;
    },
    headers?: {}
  ) => {
    return api.post<Paged<Account>>(
      `/api/users/list?size=${size}&page=${page - 1}`,
      {
        email,
      },
      headers
    );
  },

  addUser: async (account: PartialAccount) => {
    return api.post<Account>(`/api/users`, {
      ...account,
    });
  },

  getAllAccounts: async () => {
    return api.get<Account[]>(`/api/users/list/all`);
  },

  getUserById: async (account: PartialAccount) => {
    return api.get<Account>(`/api/users/${account.id}`);
  },

  deleteUser: async (account: PartialAccount) => {
    return api.delete(`/api/users/${account.id}`);
  },

  updateUser: async (account: PartialAccount) => {
    return api.put(`/api/users/${account.id}`, {
      ...account,
    });
  },

  resetPassword: async (account: PartialAccount) => {
    await api.post(
      `/api/users/reset`,
      {
        email: account.email,
      },
      {
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    );
  },

  resendActivation: async (account: PartialAccount) => {
    return api.post<Account>(
      `/api/users/resend-activate`,
      {
        email: account.email,
      },
      {
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    );
  },

  renew: async (account: PartialAccount) => {
    return api.post(
      `/api/users/renew`,
      {
        password: account.password,
        token: account.token,
      },
      {
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    );
  },

  activate: async (account: PartialAccount) => {
    return api.post(
      `/api/users/activate`,
      {
        password: account.password,
        token: account.token,
      },
      {
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    );
  },

  verify: async (token: string) => {
    return api.post(
      `/api/users/verify`,
      {
        token,
      },
      {
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    );
  },

  inviteUser: async (params: UserInviteParams) => {
    return api.post<UserContactAddress>(`/api/users/invite`, params);
  },

  switchUser: async (params: SiteRoleEnum) => {
    return api.post<string>(`/api/switch/${params}`);
  },

  /**
   * Recognition state for this user's Canva brand. Kept off the global error handler: the
   * confirmation page falls back to the recognized copy rather than toasting at the buyer.
   */
  getCanvaBrandLink: async () => {
    return api.get<CanvaBrandLink>(
      `/api/users/me/canva-brand-link`,
      {},
      {},
      { useGlobalErrorHandler: false }
    );
  },

  updateCanvaProfile: async (params: {
    firstName: string;
    lastName: string;
    email: string;
  }) => {
    return api.put<Account>(
      `/api/users/me/canva-profile`,
      params,
      {},
      {
        useGlobalErrorHandler: false,
      }
    );
  },
};

export default accountService;
